/**
 * Boss result popups - defeat and escape screens
 */

import { getBossDef } from '../game/data/boss.js';
import { calcBossReward } from '../game/systems/boss.js';
import { getTotalCps } from '../game/systems/production.js';
import { formatNumber } from '../utils/format.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { dispatch, getState, requestRender } from './renderer.js';
import { AdManager, getAdRewards } from '../monetization/index.js';
import type { BossReward } from '../game/data/boss.js';

/** Show boss defeated reward popup inside an existing overlay element */
export function showBossDefeatPopup(
  overlayEl: HTMLElement,
  closeFn: () => void,
  _reopenFn: () => void,
): void {
  const state = getState();
  const boss = state.boss.current;
  if (!boss) return;

  const def = getBossDef(boss.bossId);
  if (!def) return;

  const cps = getTotalCps(state);
  const reward = calcBossReward(boss, def, cps, state.boss.bossKillStreak);

  overlayEl.innerHTML = `
    <div class="boss-container boss-result">
      <div class="boss-result-header boss-victory">${t('boss_eliminated')}</div>
      <div class="boss-result-desc">
        ${t('boss_eliminated_desc').replace('{name}', t(def.nameKey))}
      </div>
      <div class="boss-reward-list">${buildRewardLines(reward)}</div>
      <div class="boss-result-btns">
        <button class="modal-btn primary" id="boss-claim-btn">${t('boss_claim')}</button>
        <button class="ad-btn boss-ad-btn" id="boss-claim-double-btn">${t('boss_claim_double')}</button>
      </div>
    </div>
  `;

  document.getElementById('boss-claim-btn')?.addEventListener('click', () => {
    dispatch({ type: 'CLAIM_BOSS_REWARD' });
    playSfx('prestige');
    closeFn();
    requestRender();
  });

  document.getElementById('boss-claim-double-btn')?.addEventListener('click', () => {
    const rewards = getAdRewards();
    AdManager.showRewardedAd(
      rewards.INCOME_X5,
      () => {
        dispatch({ type: 'AD_BOSS_REWARD_DOUBLE' });
        playSfx('prestige');
        closeFn();
        requestRender();
      },
      () => {
        dispatch({ type: 'CLAIM_BOSS_REWARD' });
        closeFn();
        requestRender();
      },
    );
  });

  playSfx('conquest');
}

/** Show boss escaped popup inside an existing overlay element */
export function showBossEscapePopup(
  overlayEl: HTMLElement,
  closeFn: () => void,
  reopenFn: () => void,
): void {
  const state = getState();
  const boss = state.boss.current;
  if (!boss) return;

  const canRevive = !boss.reviveUsed;

  overlayEl.innerHTML = `
    <div class="boss-container boss-result">
      <div class="boss-result-header boss-defeat">${t('boss_escaped')}</div>
      <div class="boss-result-desc">${t('boss_escaped_desc')}</div>
      <div class="boss-result-btns">
        ${canRevive ? `<button class="ad-btn boss-ad-btn" id="boss-revive-btn">${t('boss_revive')}</button>` : ''}
        <button class="modal-btn secondary" id="boss-close-btn">${t('close')}</button>
      </div>
    </div>
  `;

  document.getElementById('boss-revive-btn')?.addEventListener('click', () => {
    const rewards = getAdRewards();
    AdManager.showRewardedAd(
      rewards.INCOME_X5,
      () => {
        dispatch({ type: 'AD_BOSS_REVIVE' });
        playSfx('boost');
        closeFn();
        reopenFn();
      },
      () => { /* cancelled */ },
    );
  });

  document.getElementById('boss-close-btn')?.addEventListener('click', () => {
    dispatch({ type: 'BOSS_EXPIRED' });
    closeFn();
    requestRender();
  });
}

/** Build reward description lines */
function buildRewardLines(reward: BossReward): string {
  const lines: string[] = [];
  if (reward.ip > 0) lines.push(t('boss_reward_ip').replace('{amount}', formatNumber(reward.ip)));
  if (reward.docs > 0) lines.push(t('boss_reward_docs').replace('{amount}', String(reward.docs)));
  if (reward.gc > 0) lines.push(t('boss_reward_gc').replace('{amount}', String(reward.gc)));
  if (reward.boostSeconds > 0) lines.push(t('boss_reward_boost').replace('{seconds}', String(reward.boostSeconds)));
  return lines.map(l => `<div class="boss-reward-line">${l}</div>`).join('');
}
